import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { usePartnersReadIntegration } from '@/api/hooks';
import { type Partner } from '@/api/routes/partners/partners.api';
import Chip from '@/components/Chip';
import LoadErrorState from '@/components/LoadErrorState';
import IntegrationActivityLog from '@/domain/integration/components/IntegrationActivityLog';
import IntegrationDocs from '@/domain/integration/components/IntegrationDocs';
import IntegrationIndicators from '@/domain/integration/components/IntegrationIndicators';
import IntegrationKeyCard from '@/domain/integration/components/IntegrationKeyCard';
import IntegrationSkeleton from '@/domain/integration/components/IntegrationSkeleton';
import { type IntegrationPeriod } from '@/domain/integration/types';
import { getDisplayedKey, getKeyState } from '@/domain/integration/utils';
import { useTranslate } from '@/state/i18n';
import { ScreenRoutes } from '@/types/router';

interface Props {
  partner: Partner;
}

/**
 * `/integration` (P17): the sports media platform's page. How to integrate, the key the Bodyprint
 * team created and what the integration delivered. Without a key the page does not exist.
 */
const Integration = ({ partner }: Props) => {
  const t = useTranslate('domain.integration.Integration');
  const { data, isLoading, isError, refetch } = usePartnersReadIntegration();
  const [period, setPeriod] = useState<IntegrationPeriod>('last30Days');

  if (isLoading) return <IntegrationSkeleton />;
  if (isError || !data) {
    return <LoadErrorState message={t('loadError')} onRetry={() => refetch()} />;
  }
  if (!data.enabled) return <Navigate to={ScreenRoutes.EVENTS} replace />;

  const apiKey = getDisplayedKey(data.keys);
  const keyState = apiKey ? getKeyState(apiKey) : 'revoked';

  return (
    <Stack gap={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        gap={2}
      >
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Typography component="h1" variant="h2">
            {t('title')}
          </Typography>
          <Chip
            size="xsmall"
            label={t(`state.${keyState}`)}
            color={keyState === 'active' ? 'success' : 'neutral'}
            variant={keyState === 'active' ? 'solid' : 'outline'}
          />
        </Stack>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={period}
          onChange={(_, value: IntegrationPeriod | null) => value && setPeriod(value)}
          aria-label={t('period')}
        >
          <ToggleButton value="last30Days">{t('last30Days')}</ToggleButton>
          <ToggleButton value="total">{t('total')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {keyState === 'revoked' ? (
        <Alert severity="warning">
          <AlertTitle>{t('revoked.title')}</AlertTitle>
          {t('revoked.text')}
        </Alert>
      ) : null}

      <IntegrationIndicators
        partnerName={partner.displayName}
        subjectsAvailable={data.stats.subjectsAvailable}
        activity={data.stats[period]}
      />

      <Box sx={sx.columns}>
        <IntegrationDocs />
        <Stack gap={3}>
          {apiKey ? <IntegrationKeyCard apiKey={apiKey} /> : null}
          <IntegrationActivityLog log={data.log} />
          <Alert severity="info">
            <AlertTitle>{t('privacy.title')}</AlertTitle>
            {t('privacy.text')}
          </Alert>
        </Stack>
      </Box>
    </Stack>
  );
};

export default Integration;

const sx = {
  columns: {
    display: 'grid',
    gap: 3,
    alignItems: 'start',
    gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 2fr) minmax(0, 1fr)' },
  },
};
